/**
 * Route -> tour resolver (EPIC #1952 Wave 1, issue #1953).
 *
 * The SINGLE source of truth mapping the current SPA route to the guided tour
 * the header launcher (``TourLauncher``) should start. The launcher reads the
 * live react-router ``location.pathname``, asks this resolver for the tour id,
 * and -- if one exists and the engine's gate says it is available for this user
 * -- offers the "Take a tour of this page" button. When no tour is registered
 * for the route, the launcher hides itself (never a broken / empty tour).
 *
 * Why a dedicated resolver (not the existing registry): the registry keys tours
 * by id; it does NOT know which ROUTE a tour belongs to, and the same landing
 * route (``/crm/dashboard``) is the home of several persona welcome tours. This
 * module owns the route dimension, leaving the registry the id dimension.
 *
 * ===========================================================================
 * EXTENSION POINT for the CRM tour-authoring issues (#1955 admin, #1956 agent /
 * back-office). This is the ONE place to wire a newly-authored page tour to its
 * route. To add coverage for a page:
 *   1. Author the TourDefinition in the relevant ``*SectionTours.ts`` /
 *      ``welcomeTours.ts`` file and register it (those files already spread into
 *      the central TOUR_REGISTRY).
 *   2. Add a single ``[route]: tourId`` entry to ``STATIC_ROUTE_TOURS`` below
 *      (an exact pathname), or -- for a family of routes that share one tour --
 *      a ``{ prefix, tourId }`` entry to ``PREFIX_ROUTE_TOURS``.
 * That is all -- the launcher picks it up with no further wiring. Keep entries
 * grouped (admin / agent / back-office) and alphabetical-ish within a group so
 * the map stays scannable as coverage grows.
 * ===========================================================================
 *
 * The resolver is PURELY a route->id lookup. Whether the resolved tour is
 * actually OFFERED (audience + step gating for the current persona/modules) is
 * decided by the engine's ``isTourAvailable`` at the call site, exactly as the
 * Training launcher and welcome prompt already do -- so this map never needs to
 * encode permissions.
 */
import { getTour, welcomeTourIdForUser } from "./registry";

/**
 * The persona-landing routes whose tour is the CURRENT USER's own welcome tour
 * (resolved per persona via ``welcomeTourIdForUser``), not a fixed id. Several
 * personas share a landing route (``/crm/dashboard`` is home to the agent, the
 * office-admin and the custom spine; ``/admin/dashboard`` to admins), so the
 * tour id depends on WHO is asking. Mirrors ``landingPathForUser`` -- keep the
 * two in step when a persona's landing route changes.
 */
const WELCOME_LANDING_ROUTES: ReadonlySet<string> = new Set<string>([
  "/admin/dashboard",
  "/crm/dashboard",
  "/paralegal",
  "/finance",
  "/management",
  "/accounts",
  "/finance-accounts",
  // The office admin does not hold manage_users, so it shares /crm/dashboard
  // above rather than /crm/users. HR lands on /crm/compliance/agents (mirrors
  // landingPathForUser); the HR welcome tour (hr-welcome) starts there and is
  // wired below in CANONICAL_LANDING_TOURS, so an HR user on that landing
  // gets their own welcome tour. /crm/users stays a welcome-landing route for
  // the SCOPED user-managers (manager/paralegal/HR may open it), resolving
  // per persona.
  "/crm/users",
  "/crm/compliance/agents",
  // The canvasser lands on the farmbook. Its welcome tour (canvasser-welcome)
  // starts on /crm/farm and is audience-gated to the canvasser preset, so a plain
  // agent on /crm/farm is never offered it -- the resolver falls through to the
  // existing /crm/farm section page tour for them (see tourIdForRoute below).
  "/crm/farm",
]);

/**
 * The CANONICAL welcome tour for each welcome-landing route -- the tour whose
 * own first step LIVES on that route (issue #1999).
 *
 * Why this guard exists: ``welcomeTourIdForUser`` picks a tour by PERSONA, but a
 * persona's landing route is not always the route the launcher is invoked from.
 * The headline case: an ADMIN whose own landing is ``/admin/dashboard`` can view
 * the agent CRM (``/crm/dashboard``) via the ViewSwitcher. There, the per-persona
 * pick returns the admin welcome tour -- but EVERY admin-tour step routes to
 * ``/admin/dashboard``, so starting it from ``/crm/dashboard`` makes the engine
 * immediately navigate AWAY ("a flicker and then nothing" -- the launcher tour
 * for the page the user was on never runs and they are yanked to another page).
 *
 * The launcher's contract is "tour of THIS page", so a welcome-landing route must
 * resolve to a tour that actually belongs to it. This map names the route's own
 * canonical welcome tour; ``tourIdForRoute`` falls back to it whenever the
 * per-persona pick would route off the current page. The per-persona pick still
 * wins on a persona's OWN landing route (an office-admin / custom-spine user on
 * ``/crm/dashboard`` keeps their own welcome tour, whose first step is also on
 * ``/crm/dashboard``), so this is a strict NO-OP for every in-place persona.
 */
export const CANONICAL_LANDING_TOURS: Readonly<Record<string, string>> = {
  "/admin/dashboard": "admin-welcome",
  "/crm/dashboard": "agent-welcome",
  "/paralegal": "paralegal-welcome",
  "/finance": "finance-welcome",
  "/management": "manager-welcome",
  "/accounts": "accounts-welcome",
  "/finance-accounts": "finance-accounts-welcome",
  // #2847: the office-admin welcome tour moved to /crm/dashboard (the page
  // resolver's per-persona pick handles it there); /crm/users has no single
  // canonical welcome tour any more -- a scoped user-manager opening the
  // launcher on /crm/users falls through to the page/section resolution.
  // #2452: HR lands on the agent-compliance roster; its own welcome tour
  // (hr-welcome) starts there. Audience-gated to the hr preset, so a manager who
  // reaches the same route via read-only oversight is never offered it.
  "/crm/compliance/agents": "hr-welcome",
  // #2467: the canvasser lands on the farmbook; its own welcome tour
  // (canvasser-welcome) starts there. Audience-gated to the canvasser preset, so
  // a plain agent on /crm/farm keeps the /crm/farm section page tour instead (the
  // per-persona pick returns agent-welcome which is off-route, and the resolver
  // then falls through to the STATIC_ROUTE_TOURS entry before this canonical).
  "/crm/farm": "canvasser-welcome",
};

/**
 * True when ``tourId`` resolves to a tour whose FIRST step lives on ``route`` --
 * i.e. starting it from ``route`` will not bounce the user to another page
 * (issue #1999). An unknown/empty id is treated as not-on-route.
 */
function tourStartsOnRoute(tourId: string, route: string): boolean {
  const tour = getTour(tourId);
  return Boolean(tour && tour.steps[0]?.route === route);
}

/**
 * The tour id for the LONGEST ``PREFIX_ROUTE_TOURS`` entry that matches
 * ``pathname`` on a path-segment boundary, or ``null`` when none. Extracted so
 * both the welcome-landing fall-through (#2467) and the normal resolution tail
 * share one segment-safe prefix matcher.
 */
function longestPrefixTour(pathname: string): string | null {
  let best: { prefix: string; tourId: string } | null = null;
  for (const entry of PREFIX_ROUTE_TOURS) {
    if (
      (pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)) &&
      (!best || entry.prefix.length > best.prefix.length)
    ) {
      best = entry;
    }
  }
  return best?.tourId ?? null;
}

/**
 * Exact-pathname -> tour id. The section deep-dive tours each declare a section
 * LANDING route as their first step's route; that route launches the tour. (The
 * admin section tours all open on ``/admin/dashboard`` -- a welcome-landing route
 * already covered above by the persona welcome tour -- so they are intentionally
 * reached from Training, not the header, to keep one obvious tour per route.)
 *
 * #1955 (admin pages) and #1956 (agent / back-office pages) extend THIS map.
 */
export const STATIC_ROUTE_TOURS: Readonly<Record<string, string>> = {
  // --- Agent CRM section deep-dives (#1956 extends) ---
  "/crm/contacts": "agent-prospecting",
  "/crm/listings": "agent-listings",
  "/crm/deals": "agent-deals",
  "/crm/kpis": "agent-my-stats",
  "/crm/tasks": "agent-workspace",
  "/crm/document-vault": "agent-comms-docs",

  // --- Admin pages (#1955) ---
  // One focused per-page tour for each COMPLEX admin page (filters + charts +
  // drill-in / a multi-mode editor / a guarded workflow). WYSIWYG admin pages
  // (a flat list / single form) are intentionally absent -- the launcher hides
  // itself there. NB ``/admin/dashboard`` is a WELCOME_LANDING_ROUTE (the admin
  // welcome tour), so it is deliberately NOT registered here.
  "/admin/commission": "admin-commission-page",
  "/admin/sales-targets": "admin-sales-targets-page",
  "/admin/leaderboard": "admin-leaderboard-page",
  "/admin/monthly-deals": "admin-monthly-deals-page",
  "/admin/market-share": "admin-market-share-page",
  "/admin/deal-pipeline": "admin-deal-pipeline-page",
  "/admin/attorney-pipeline": "admin-attorney-pipeline-page",
  "/admin/kpis": "admin-kpi-report-page",
  "/admin/kpi-metrics": "admin-kpi-metrics-page",
  "/admin/kpi-tiers": "admin-kpi-tiers-page",
  "/admin/compliance": "admin-fica-page",
  "/admin/communications": "admin-communications-page",
  "/admin/users": "admin-user-management-page",
  "/admin/contacts/dedup": "admin-contact-dedup-page",
  "/admin/integrations": "admin-integrations-page",
  "/admin/commission-sheet": "admin-commission-sheet-page",
  "/admin/kpi-upload": "admin-kpi-upload-page",
  // Admin pages deliberately NOT tour-wired (#2452 triage): /admin/deals/dedup
  // (Duplicate Deals) + /admin/commission-authorizations (Privileged
  // Authorizations) + /admin/pipeline-cashflow + /admin/bond-analytics (Bonds by
  // Bank) + /admin/kpi-scorecard + /admin/kpi-exceptions are single-purpose
  // list/stat pages (WYSIWYG) or are covered by the admin section tours -- the
  // launcher hides itself there by design.

  // --- Manager oversight (#2452) ---
  // The manager reaches these read-only company dashboards via the
  // /crm/oversight/* keep-in-shell mounts (App.tsx). Each resolves to the
  // read-only OVERSIGHT VARIANT of the matching admin page tour (adminPageTours
  // OVERSIGHT_PAGE_TOURS), whose steps route to the /crm/oversight/* mount and
  // whose audience is view_management_dashboards -- so the launcher never
  // navigates a manager to the /admin/* route (AdminRoute would 403, the #1999
  // off-route bounce).
  "/crm/oversight/monthly-deals": "oversight-monthly-deals",
  "/crm/oversight/deal-pipeline": "oversight-deal-pipeline",
  "/crm/oversight/attorney-pipeline": "oversight-attorney-pipeline",
  "/crm/oversight/compliance": "oversight-compliance",
  "/crm/oversight/kpis": "oversight-kpis",
  "/crm/oversight/market-share": "oversight-market-share",
  "/crm/oversight/communications": "oversight-communications",
  // Oversight routes deliberately NOT tour-wired (#2452 triage): pipeline-cashflow,
  // fica-outstanding, kpi-exceptions, kpi-scorecard have no base page tour;
  // kpi-metrics/kpi-tiers/kpi-upload/areas/checklist-templates are CONFIG surfaces
  // whose admin tours walk edit/upload actions a read-only manager cannot perform
  // (a route-remap would surface an edit prompt), and activity/agent-usage/
  // bond-analytics are single-view stat pages -- all covered by the grouped
  // Oversight welcome step above rather than a per-page walk.
  // Agent-compliance roster (#2452). The SAME AgentComplianceRoster page is
  // mounted at BOTH /admin/compliance/agents (admin shell) and
  // /crm/compliance/agents. The admin route gets the admin-shell variant, whose
  // steps route to /admin/compliance/agents (a tour's first step must live on
  // the route it is wired to, or the launcher hides itself). The
  // /crm/compliance/agents route is a WELCOME_LANDING_ROUTE that resolves to the
  // HR welcome tour; the CRM-routed back-office page tour is reachable from
  // Training there.
  "/admin/compliance/agents": "admin-agent-compliance-page",

  // --- Agent CRM pages (#1956) ---
  // One focused per-page tour for each COMPLEX agent page NOT already covered by
  // an agent SECTION tour. WYSIWYG record-detail editors (contact/listing/buyer
  // detail) and the Settings page are intentionally absent -- the launcher hides
  // itself there. The deal DETAIL page is covered by a PREFIX entry below (the
  // exact ``/crm/deals`` list keeps the section tour).
  "/crm/farm": "agent-farm-page",
  "/crm/buyers": "agent-buyers-page",
  "/crm/appointments": "agent-appointments-page",
  "/crm/messaging": "agent-messaging-page",
  "/crm/fica-matrix": "agent-fica-matrix-page",
  "/crm/referrals": "agent-referrals-page",
  // Conveyancers is capture_deal-gated (ConveyancerRoute), so its tour is a
  // back-office desk tour (#1956 fix).
  "/crm/conveyancers": "back-office-conveyancers-page",
  "/crm/connect": "agent-connect-page",

  // --- Back-office pages (#1956) ---
  // The Behind Schedule desk page (capture_deal-gated). The File Cover Page is a
  // ``/:otpId`` detail route covered by a PREFIX entry below. The single-surface
  // back-office WELCOME landings (/paralegal /finance /accounts /management
  // /finance-accounts) keep their persona welcome tour (WELCOME_LANDING_ROUTES,
  // #1521) and are deliberately NOT registered here.
  "/behind-schedule": "back-office-behind-schedule",
};

/**
 * Prefix -> tour id, for a family of routes that share ONE tour (e.g. a list
 * page and its ``/:id`` detail page). Matched only when no exact entry wins, by
 * longest prefix first, and only on a path SEGMENT boundary (``/crm/deals``
 * matches ``/crm/deals`` and ``/crm/deals/42`` but never ``/crm/dealsX``). Empty
 * today -- the section tours land on their list page -- but the mechanism is here
 * so #1955/#1956 can cover a detail-only page without inventing a new pattern.
 */
export const PREFIX_ROUTE_TOURS: ReadonlyArray<{ prefix: string; tourId: string }> = [
  // Deal DETAIL (#1956): the exact ``/crm/deals`` wins via STATIC_ROUTE_TOURS
  // (the deals section tour), so this prefix only catches ``/crm/deals/:id`` and
  // ``/crm/deals/add-sale``. The deal-file tour opens with a ``center`` step so
  // it is robust on the add-sale form too (its anchor steps simply advance there).
  { prefix: "/crm/deals", tourId: "agent-deal-detail-page" },
  // File Cover Page: ``/legal/:otpId`` is the only detail route; the bare
  // ``/legal`` queue list ALSO matches this PREFIX (segment-boundary matching
  // only cares that the path starts with ``/legal``). Gated on ``capture_deal``
  // so only the paralegal/back-office desk is ever offered it.
  //
  // This tour is RECORD-SCOPED: every step, including the opener, routes to
  // the ``/legal/:otpId`` PATTERN, and ``routeMatchesStep("/legal",
  // "/legal/:otpId")`` is FALSE (the queue path is one segment short of the
  // pattern) -- so the bare ``/legal`` queue is never a valid start route for
  // it. Three things enforce that: (1) ``TourLauncher`` checks
  // ``routeMatchesStep`` against this tour's opening route, so the "Take a
  // tour of this page" button is ABSENT on the bare ``/legal`` queue (this
  // prefix entry still resolves the id for a genuine ``/legal/:otpId`` detail
  // page, where the concrete path DOES match the pattern); (2)
  // ``GuidedToursPanel`` excludes any ``:param``-first-step tour from the
  // Training section list; (3) the engine's own nav effect ends the tour
  // gracefully (rather than navigating to the literal ``:otpId`` pattern) if
  // a ``:param``-first-step tour is ever reached off-route by some other path
  // (e.g. a ``?tour=`` deep-link).
  { prefix: "/legal", tourId: "back-office-file-cover-page" },
];

/**
 * Resolve the tour id registered for ``pathname`` for the given user, or
 * ``null`` when none. ``role`` + ``preset`` are needed only to pick the persona
 * welcome tour on a shared landing route; section routes ignore them.
 *
 * Resolution order: persona welcome landing -> exact static route -> longest
 * matching prefix -> none.
 */
export function tourIdForRoute(
  pathname: string,
  role: string | undefined,
  preset: string | null | undefined,
): string | null {
  if (WELCOME_LANDING_ROUTES.has(pathname)) {
    // Per-persona pick first -- but only if its first step actually lives on
    // THIS route, so the launcher never starts a tour that bounces the user to
    // another page (issue #1999: an admin viewing /crm/dashboard via the
    // ViewSwitcher would otherwise get the /admin/dashboard-routed admin tour
    // and be yanked away -- the "flicker and then nothing"). Otherwise fall back
    // to the route's OWN canonical welcome tour, which is on-route by definition.
    const personaTour = welcomeTourIdForUser(role, preset);
    if (tourStartsOnRoute(personaTour, pathname)) {
      return personaTour;
    }
    // #2467: when the per-persona pick is off-route, prefer this route's OWN
    // static/prefix section tour before the canonical welcome fallback. This
    // matters for a route that is BOTH a welcome landing AND a section page --
    // /crm/farm is the canvasser's welcome landing but also the plain agent's
    // ``agent-farm-page`` section tour. A plain agent's off-route agent-welcome
    // pick must resolve to the farm section tour, not the audience-gated
    // canvasser-welcome (which the launcher would then hide, losing the farm
    // tour). The canonical welcome remains the final fallback (e.g. an admin on
    // /crm/dashboard, which has no static entry -> agent-welcome, #1999).
    const landingStatic = STATIC_ROUTE_TOURS[pathname];
    if (landingStatic) return landingStatic;
    const landingPrefix = longestPrefixTour(pathname);
    if (landingPrefix) return landingPrefix;
    return CANONICAL_LANDING_TOURS[pathname] ?? null;
  }
  const exact = STATIC_ROUTE_TOURS[pathname];
  if (exact) return exact;

  // Longest-prefix wins, segment-boundary safe.
  return longestPrefixTour(pathname);
}
